import { getProjectColor as getColor } from '../utils/dashboardUtils';
import { formatUserDisplayName as formatName } from '../../../lib/utils';
import { useCallback, useMemo } from 'react';
import { logger } from '../../../utils/logger';
import { FILTER_CONSTANTS } from '../constants/filterConstants';
import { DashboardFiltersState } from '../../../hooks/queries/dashboardTypes';

/**
 * Represents a user ID or special filter ID for dashboard filtering
 * This helps distinguish between regular user IDs and special filter constants
 */
type UserFilterId = string | typeof FILTER_CONSTANTS.SHARED_UNASSIGNED;

interface UseDashboardFiltersProps {
  activeFilters: DashboardFiltersState;
  setUserFilter: (userIds: string[]) => void;
  setProjectFilter: (projectIds: string[]) => void;
  setTagFilter: (tags: string[]) => void;
  resetFilters: () => void;
  saveDashboardFilters: (filters: DashboardFiltersState | null) => void;
  loadDashboardFilters: () => void;
  users: any[];
  projects: any[];
  availableTags: string[];
}

/**
 * useDashboardFilters - Simple adapter hook that converts store filter state
 * to UI filter components state. All filter state is managed in the store.
 *
 * IMPORTANT: This hook must receive data as props rather than calling useDashboardData()
 * to ensure a single source of truth for filter state. Calling the hook internally
 * would create a separate state instance that doesn't sync with the main dashboard.
 *
 * @param props - Filter state and actions from the parent useDashboardData hook
 */
export const useDashboardFilters = ({
  activeFilters,
  setUserFilter,
  setProjectFilter,
  setTagFilter,
  resetFilters,
  saveDashboardFilters,
  loadDashboardFilters,
  users,
  projects,
  availableTags: storeAvailableTags,
}: UseDashboardFiltersProps) => {
  // Create project filter UI objects from store data
  // Projects are already filtered by the Edge function to only include those with deadline tasks
  const projectFilters = useMemo(() => {
    // Sort projects by last_selected_at (most recent first)
    const sortedProjects = [...(projects || [])].sort((a, b) => {
      // If last_selected_at is missing, use updated_at as fallback
      const aTime = a.last_selected_at || a.updated_at || '';
      const bTime = b.last_selected_at || b.updated_at || '';

      // Latest date first (descending order)
      return aTime > bTime ? -1 : aTime < bTime ? 1 : 0;
    });

    // Map to UI filter objects
    return sortedProjects.map(project => ({
      id: project.id,
      name: project.name,
      color: getColor(project.id),
      active: activeFilters.projectIds.includes(project.id),
      hasTasks: true,
    }));
  }, [projects, activeFilters.projectIds]);

  // Create user filter UI objects from store data
  const userFilters = useMemo(
    () =>
      (users || []).map(user => ({
        id: user.id,
        name: formatName(user.display_name) || user.email?.split('@')[0] || 'Unknown',
        email: user.email || '',
        avatar_url: user.avatar_url,
        active: activeFilters.userIds.includes(user.id),
      })),
    [users, activeFilters.userIds]
  );

  // Toggle project filter handler - improved dependency handling
  const toggleProjectFilter = useCallback(
    (projectId: string, isExclusive = true) => {
      // Get current filter state from the activeFilters directly to avoid stale closure values
      const currentProjectIds = activeFilters.projectIds;
      const isCurrentlyActive = currentProjectIds.includes(projectId);

      if (isExclusive) {
        // Exclusive mode - only this project or none
        setProjectFilter(isCurrentlyActive ? [] : [projectId]);
      } else {
        // Toggle mode - add or remove from current selection
        const newProjectIds = isCurrentlyActive
          ? currentProjectIds.filter(id => id !== projectId)
          : [...currentProjectIds, projectId];

        setProjectFilter(newProjectIds);
      }
    },
    [activeFilters.projectIds, setProjectFilter] // Include dependencies
  );

  /**
   * Toggles a user filter on or off, with special handling for the shared-unassigned filter
   *
   * @param userId - The ID of the user to toggle, or FILTER_CONSTANTS.SHARED_UNASSIGNED
   * @param isExclusive - If true, this filter will deselect all other filters (always true for users)
   */
  const toggleUserFilter = useCallback(
    (userId: UserFilterId, _isExclusive = true) => {
      // Get fresh state from activeFilters to avoid stale closure values
      const currentUserIds = activeFilters.userIds;
      const isCurrentlyActive = currentUserIds.includes(userId);

      // Special case for the shared-unassigned filter
      if (userId === FILTER_CONSTANTS.SHARED_UNASSIGNED) {
        logger.component('Toggling shared-unassigned filter', {
          currentlyActive: isCurrentlyActive,
          currentUserIds,
        });

        // When activating shared-unassigned, remove all other user filters
        // When deactivating, just remove shared-unassigned
        if (isCurrentlyActive) {
          // If already active, just remove it
          setUserFilter(currentUserIds.filter(id => id !== FILTER_CONSTANTS.SHARED_UNASSIGNED));
        } else {
          // If not active, enable only this filter (clear all others)
          setUserFilter([FILTER_CONSTANTS.SHARED_UNASSIGNED]);
        }
        return;
      }

      // Regular user ID handling - always use exclusive mode
      // This means selecting a user will deselect all other users

      // If this user is already active, toggle it off
      if (isCurrentlyActive) {
        setUserFilter([]);
      } else {
        // If not active, make this the only selected user (clear all others)
        // But keep shared-unassigned filter if it's there
        setUserFilter([userId]);
      }
    },
    [activeFilters.userIds, setUserFilter] // Include dependencies
  );

  // Toggle tag filter - simplified with minimized dependencies
  const toggleTagFilter = useCallback(
    (tag: string) => {
      // Get fresh state from activeFilters
      const currentTags = activeFilters.tags;
      const isTagActive = currentTags.includes(tag);

      // Toggle tag state
      const newTags = isTagActive ? currentTags.filter(t => t !== tag) : [...currentTags, tag];

      setTagFilter(newTags);
    },
    [activeFilters.tags, setTagFilter] // Include dependencies
  );

  /**
   * Return value containing filter state and actions
   */
  return {
    // State
    projectFilters,
    userFilters,
    selectedTags: activeFilters.tags,
    availableTags: storeAvailableTags,

    // Actions
    toggleUserFilter,
    toggleProjectFilter,
    toggleTagFilter,
    setSelectedTags: (tags: string[]) => setTagFilter(tags),
    resetFilters,
    saveDashboardFilters,
    loadDashboardFilters,

    // For compatibility
    activeUserIds: activeFilters.userIds,
    activeProjectIds: activeFilters.projectIds,
  };
};
